<template>
  <div class="user-edit">
    <div v-if="userDetail" class="uk-grid-medium" uk-grid>
      <div class="uk-width-5-5">
        <div class="d-flex">
          <nav id="breadcrumbs" class="mb-3">
            <ul>
              <li>
                <router-link :to="{ name: 'company.dashboard' }">
                  <i class="uil-home-alt"></i>
                </router-link>
              </li>
              <li>{{ $t("users.users") }}</li>
              <li>{{ $t("users.edit_user") }}</li>
            </ul>
          </nav>
        </div>

        <div v-if="formErrors && formErrors.length" class="card">
          <div v-for="error in formErrors" class="uk-alert-danger" uk-alert>
            <a class="uk-alert-close" uk-close></a>
            <p style="color: red">
              <i class="uil-exclamation-triangle"></i> {{ error }}
            </p>
          </div>
        </div>

        <div class="card mt-4">
          <form action="" class="regular-form">
            <div class="uk-grid-collapse uk-child-width-1-2@s" uk-grid>
              <div class="uk-padding-large position-relative">
                <h4>
                  <i class="uil-user-circle"></i> {{ $t("users.information") }}
                </h4>
                <div class="uk-position-relative my-4">
                  <label class="col-form-label" for="name">{{ $t("users.photo") }}</label>
                  <div class="user-profile-photo m-auto rounded-circle bg-light">
                    <img id="profile-image-holder" :src="form.photo_link" alt="" class="border border-light" />
                  </div>
                  <div class="uk-position-center">
                    <file-upload ref="fileUpload" :accept="'image/*'" :multiple="false" :title="''"
                      class="user-photo-upload" @input="onFileUpload" />
                    <span class="icon-feather-camera icon-medium text-black mt-4 user-photo-camera-icon"
                      @click="triggerFileUpload"></span>
                  </div>
                </div>

                <div class="uk-grid-small" uk-grid>
                  <div class="uk-width-1-2@m">
                    <sf-input v-model="form.name" :label="$t('users.firstname')" :required="true" name="name"
                      type="text" />
                  </div>
                  <div class="uk-width-1-2@m">
                    <sf-input v-model="form.surname" :label="$t('users.lastname')" :required="true" name="surname"
                      type="text" />
                  </div>
                </div>

                <sf-input v-if="!shouldHideEmailField" v-model="form.email" :label="$t('general.email')" :required="true"
                  name="mail" type="email" />

                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="gender">{{ $t("gender.gender") }}</label>
                    <select v-model="form.gender_id" class="form-control" name="gender">
                      <option :value="null">{{ $t("general.select") }}</option>
                      <option v-for="item in genders" :key="item.id" :value="item.id">
                        {{ $t("gender." + item.key) }}
                      </option>
                    </select>
                  </div>
                </div>

                <PhoneInput v-model="form.phone" :country-phone-code="form.country_phone_code"
                  :title="$t('general.phone')" name="phone" />

                <sf-input v-model="form.registration_number" :label="$t('users.reg_no')"
                  :required="form.login_type_id === 4" name="sicil" type="text" />

                <sf-input v-model="form.national_id" :required="form.login_type_id === 3" :label="$t('general.national_id')"
                  name="national_id" type="text" />

                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="dob">{{ $t("users.dob") }}</label>
                    <input id="dob" v-model="form.dob" class="form-control" name="dob" placeholder="" required=""
                      type="date" />
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="dob">{{
                      $t("profile.login_type")
                    }}</label>
                    <select v-model="form.login_type_id" class="form-control" name="login_type">
                      <option v-for="item in login_types" :value="item.id">
                        {{ item.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="dob">{{ $t("profile.authentication_type") }}</label>
                    <select v-model="form.authentication_type_id" class="form-control" name="authentication_type">
                      <option v-for="item in authentication_types" :key="item.id" :value="item.id">
                        {{ item.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label">{{ $t('general.notification_method') }}</label>
                    <select v-model="form.notification_method" class="form-control" name="notification_method">
                      <option v-for="item in notification_methods" :key="item.value" :value="item.value">
                        {{ item.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label">{{ $t("general.language") }}</label>
                    <select v-model.number="form.language_id" class="form-control" name="language">
                      <option :value="null">{{ $t("general.select") }}</option>
                      <option v-for="item in languages" :key="item.id" :value="item.id">
                        {{ item.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="form-group row mt-4">
                  <div class="col-md-12">
                    <h4>
                      <i class="uil-user-circle"></i>
                      {{ $t("users.user_add_to_group") }}
                    </h4>
                    <!-- <multiple-select-input :model.sync="userGroup" :required="false" :searchable="true"
                      :values="formattedUserGroup" @select="form.user_group = $event.id" /> -->
                    <multiselect v-model="userGroup" :allow-empty="true" :multiple="true" :options="formattedUserGroup"
                      :placeholder="$t('general.select')" :searchable="true" :tag-placeholder="$t('general.select')"
                      deselectLabel="" label="name" :selectLabel="$t('general.select')" track-by="id">
                      <template slot="option" slot-scope="option">
                        {{ option.option.name }}
                      </template>
                    </multiselect>
                  </div>
                </div>
                <hr />
                <div class="form-group row mt-3 input-checkbox">
                  <div class="col-md-12">
                    <h4>
                      <i class="uil-asterisk"></i>
                      {{ $t("users.user_password") }}
                    </h4>
                    <div class="d-flex">
                      <input id="user-password" v-model="passwordStatus" checked type="checkbox" />
                      <label class="ml-2" for="user-password">{{
                        $t("users.user_password_auto")
                      }}</label>
                    </div>
                    <label class="col-form-label" for="pass">{{
                      $t("general.password")
                    }}<span class="required">*</span></label>
                    <input id="pass" v-model="form.password" class="form-control" disabled name="pass" placeholder=""
                      type="password" />
                  </div>
                </div>
              </div>
              <div class="uk-padding-large">
                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="institution">
                      {{ $t('general.role_definition') }}
                    </label>
                    <multiselect v-model="selectedRoles" :allow-empty="true" :multiple="true" :options="roles"
                      :placeholder="$t('general.select')" :reduce="(role) => role.id" :searchable="true"
                      :tag-placeholder="$t('general.select')" deselectLabel="" label="name" :selectLabel="$t('general.select')"
                      track-by="id" />
                  </div>
                </div>
                <div class="form-group row" v-if="isHierarchy">
                  <div class="col-md-12">
                    <label class="col-form-label" for="institution">
                      {{ $t('general.linked_manager') }}
                      <span class="required">*</span>
                    </label>

                    <multiselect v-model="selectedUser" :options="users" :searchable="true" :allow-empty="true"
                      :placeholder="$t('general.select')" deselect-label="" :select-label="$t('general.select')"
                      :custom-label="userLabel" track-by="id" />
                  </div>
                </div>

                <h4>
                  <i class="uil-bag"></i> {{ $t("users.business_information") }}
                </h4>
                <div v-if="$isSuperAdmin" class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="institution">
                      {{ $t("users.institution") }}
                      <span class="required">*</span>
                    </label>
                    <multiple-select-input :model.sync="company" :required="false" :searchable="true"
                      :values="formattedInstitution" @select="
                        form.company_id = $event.id;
                      changeCompany();
                      " />
                  </div>
                </div>

                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="position">{{
                      $t("users.mission")
                    }}</label>
                    <multiple-select-input :model.sync="position" :required="false" :searchable="true"
                      :tag-placeholder="$t('general.add_tag_mission')" :taggable="true" :values="formattedPosition"
                      @addTag="addTagToMission" />
                  </div>
                </div>

                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="title">{{ $t("users.title") }}</label>
                    <multiple-select-input :model.sync="title" :required="false" :searchable="true"
                      :tag-placeholder="$t('general.add_tag_title')" :taggable="true" :values="formattedTitle"
                      @addTag="addTagToTitles" />
                  </div>
                </div>

                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="region">{{ $t("users.area") }}</label>
                    <multiple-select-input :model.sync="region" :required="false" :searchable="true"
                      :tag-placeholder="$t('general.add_tag_region')" :taggable="true" :values="formattedRegion"
                      @addTag="addTagToRegion" />
                  </div>
                </div>

                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="department">{{ $t("users.department") }}</label>
                    <multiple-select-input :model.sync="department" :required="false" :searchable="true"
                      :tag-placeholder="$t('general.add_tag_department')" :taggable="true" :values="formattedDepartment"
                      @addTag="addTagToDepartment" />
                  </div>
                </div>

                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label" for="branch">{{ $t("users.branch") }}</label>
                    <multiple-select-input :model.sync="branch" :required="false" :searchable="true"
                      :tag-placeholder="$t('general.add_tag_branch')" :taggable="true" :values="formattedBranch"
                      @addTag="addTagToBranch" />
                  </div>
                </div>

                <div class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label">{{ $t('general.company') }}</label>
                    <CorporateSelect v-model="corporate" :company-id="company_id" :multiple="false" />
                  </div>
                </div>

                <div v-if="integrationOptions.length" class="form-group row">
                  <div class="col-md-12">
                    <label class="col-form-label">{{ $t('general.integrations') || 'Entegrasyonlar' }}</label>
                    <multiselect v-model="selectedIntegrations" :allow-empty="true" :multiple="true"
                      :options="integrationOptions" :placeholder="$t('general.select')" :searchable="true"
                      :tag-placeholder="$t('general.select')" deselectLabel="" label="title" :selectLabel="$t('general.select')"
                      track-by="type">
                      <template slot="option" slot-scope="option">
                        {{ option.option.title }}
                      </template>
                    </multiselect>
                  </div>
                </div>

                <!-- <div class="form-group row mt-3 input-checkbox">
                  <div class="col-md-12">
                    <div class="d-flex">
                      <input id="user-admin" v-model="form.is_admin" checked type="checkbox" value="1" />
                      <label class="ml-2" for="user-admin">{{
                        $t("users.competent")
                      }}</label>
                    </div>
                  </div>
                </div> -->
              </div>
            </div>

            <div class="uk-card-footer pt-0">
              <div class="float-right">
                <button :disabled="isSubmitting" class="uk-button uk-button-primary small" type="button"
                  @click="formSubmit">
                  <i v-if="isSubmitting" class="uil-spinner" uk-spinner="ratio: 0.8"></i>
                  {{ isSubmitting ? `${$t('general.saving')}...` : $t('general.save') }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isHierarchy } from "@/core/services/store.js";
import DataTable from "@/assets/components/dataTable/DataTable";
import store from "@/core/services";

import company_module, {
  BASE_URL as COMPANY_BASE_URL,
  GET_ITEMS as GET_INSTITUTIONS,
  ITEMS as INSTITUTIONS,
  MODULE_NAME as COMPANY_MODULE,
} from "@/core/services/store/company.module";

import user_group_module, {
  BASE_URL as USER_GROUP_BASE_URL,
  GET_ITEMS as GET_USER_GROUPS,
  ITEMS as USER_GROUPS,
  MODULE_NAME as USER_GROUP_MODULE,
} from "@/core/services/store/user_group.module";

import position_module, {
  BASE_URL as POSITION_BASE_URL,
  CREATE_ITEM as POSITION_CREATE_ITEM,
  GET_ITEMS as GET_POSITIONS,
  ITEMS as POSITIONS,
  MODULE_NAME as POSITION_MODULE_NAME,
  MODULE_NAME as POSITION_MODULE,
} from "@/core/services/store/position.module";

import title_module, {
  BASE_URL as TITLE_BASE_URL,
  CREATE_ITEM as TITLE_CREATE_ITEM,
  GET_ITEMS as GET_TITLES,
  ITEMS as TITLES,
  MODULE_NAME as TITLE_MODULE_NAME,
  MODULE_NAME as TITLE_MODULE,
} from "@/core/services/store/title.module";

import region_module, {
  BASE_URL as REGION_BASE_URL,
  CREATE_ITEM as REGION_CREATE_ITEM,
  GET_ITEMS as GET_REGIONS,
  ITEMS as REGIONS,
  MODULE_NAME as REGION_MODULE_NAME,
  MODULE_NAME as REGION_MODULE,
} from "@/core/services/store/region.module";

import department_module, {
  BASE_URL as DEPARTMENT_BASE_URL,
  CREATE_ITEM as DEPARTMENT_CREATE_ITEM,
  GET_ITEMS as GET_DEPARTMENTS,
  ITEMS as DEPARTMENTS,
  MODULE_NAME as DEPARTMENT_MODULE_NAME,
  MODULE_NAME as DEPARTMENT_MODULE,
} from "@/core/services/store/department.module";

import branch_module, {
  BASE_URL as BRANCH_BASE_URL,
  CREATE_ITEM as BRANCH_CREATE_ITEM,
  GET_ITEMS as GET_BRANCHS,
  ITEMS as BRANCHS,
  MODULE_NAME as BRANCH_MODULE_NAME,
  MODULE_NAME as BRANCH_MODULE,
} from "@/core/services/store/branch.module";

import user_update_module, {
  BASE_URL as USER_UPDATE_BASE_URL,
  ERROR as USER_ERRORS,
  MODULE_NAME as USER_UPDATE_MODULE,
  SUCCESS,
  UPDATE_USER_POST,
} from "@/core/services/store/user.module";

import user_module, {
  GET_ITEM as GET_USER,
  ITEM as USER,
  MODULE_NAME as USER_MODULE,
} from "@/core/services/store/user_detail.module";

import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";
import PhoneInput from "@/assets/components/inputs/PhoneInput";
import FileUpload from "@/view/components/input/fileUpload/fileUpload.vue";
import CorporateSelect from "@/view/components/select/CorporateSelect.vue";
import SfInput from "@/view/components/ui/SfInput.vue";
import moment from "moment";
import axios from "axios";
import i18nService from "@/core/services/i18n.service.js";

const _USER_GROUP_MODULE_NAME = USER_GROUP_MODULE;
const _COMPANY_MODULE_NAME = COMPANY_MODULE;
const _POSITION_MODULE = POSITION_MODULE;
const _TITLE_MODULE = TITLE_MODULE;
const _REGION_MODULE = REGION_MODULE;
const _DEPARTMENT_MODULE = DEPARTMENT_MODULE;
const _BRANCH_MODULE = BRANCH_MODULE;
const _USER_MODULE = USER_MODULE;
const _USER_UPDATE_MODULE = USER_UPDATE_MODULE;

export default {
  name: "UserAdd",
  components: { DataTable, MultipleSelectInput, PhoneInput, FileUpload, SfInput, CorporateSelect },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_COMPANY_MODULE_NAME, company_module);
    registerStoreModule(_USER_GROUP_MODULE_NAME, user_group_module);
    registerStoreModule(_POSITION_MODULE, position_module);
    registerStoreModule(_TITLE_MODULE, title_module);
    registerStoreModule(_REGION_MODULE, region_module);
    registerStoreModule(_DEPARTMENT_MODULE, department_module);
    registerStoreModule(_BRANCH_MODULE, branch_module);
    registerStoreModule(_USER_MODULE, user_module);
    registerStoreModule(_USER_UPDATE_MODULE, user_update_module);
  },
  data() {
    return {
      users: [],
      user_admin: false,
      passwordStatus: true,
      genders: [],
      languages: [],
      company: {
        id: null,
        name: null,
      },
      userGroup: {
        id: null,
        name: null,
      },
      position: {
        id: null,
        name: null,
      },
      title: {
        id: null,
        name: null,
      },
      region: {
        id: null,
        name: null,
      },
      department: {
        id: null,
        name: null,
      },
      branch: {
        id: null,
        name: null,
      },
      corporate: null,
      form: {
        name: "",
        surname: "",
        email: "",
        phone: "",
        dob: null,
        registration_number: "",
        user_group: null,
        company_id: null,
        branch_id: null,
        department_id: null,
        region_id: null,
        title_id: null,
        gender_id: null,
        position_id: null,
        corporate_id: null,
        password: "",
        // is_admin: false,
        login_type_id: 1,
        store_id: null,
        national_id: "",
        notification_method: "email",
        language_id: null,
        photo: null,
        country_phone_code: '',
        parent_id: '',
      },
      formErrors: [],
      company_id: null,
      isSubmitting: false,
      login_types: [],
      authentication_types: [],
      notification_methods: [
        { name: this.$t("general.notify_via_email"), value: "email" },
        { name: this.$t("general.notify_via_sms"), value: "sms" },
      ],
      roles: [],
      selectedRoles: [],
      selectedUser: [],
      selectedParentId: '',
      integrationOptions: [],
      selectedIntegrations: [],
    };
  },
  computed: {
    isHierarchy() {
      return isHierarchy.value;
    },
    shouldHideEmailField() {
      const companyId = this.form?.company_id || this.company_id || this.company?.id;
      return Number(companyId) === 489;
    },
    errors() {
      return store.getters[USER_UPDATE_MODULE + "/" + USER_ERRORS];
    },
    success() {
      return store.getters[USER_UPDATE_MODULE + "/" + SUCCESS];
    },
    userDetail: {
      get() {
        return store.getters[USER_MODULE + "/" + USER];
      },
      set(value) {
      },
    },
    userInfo: {
      get: function () {
        return JSON.parse(this.secureLocalStorage("user"));
      },
      set: function (newValue) {
      },
    },
    branchs: {
      get() {
        return store.getters[BRANCH_MODULE + "/" + BRANCHS];
      },
      set(value) {
      },
    },
    departments: {
      get() {
        return store.getters[DEPARTMENT_MODULE + "/" + DEPARTMENTS];
      },
      set(value) {
      },
    },
    regions: {
      get() {
        return store.getters[REGION_MODULE + "/" + REGIONS];
      },
      set(value) {
      },
    },
    titles: {
      get() {
        return store.getters[TITLE_MODULE + "/" + TITLES];
      },
      set(value) {
      },
    },
    positions: {
      get() {
        return store.getters[POSITION_MODULE + "/" + POSITIONS];
      },
      set(value) {
      },
    },
    user_groups: {
      get() {
        return store.getters[USER_GROUP_MODULE + "/" + USER_GROUPS];
      },
      set(value) {
      },
    },
    institutions: {
      get() {
        return store.getters[COMPANY_MODULE + "/" + INSTITUTIONS];
      },
      set(value) {
      },
    },
    formattedBranch() {
      let results = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.branchs === null) return [];
      this.branchs.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedDepartment() {
      let results = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.departments === null) return [];

      this.departments.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedRegion() {
      let results = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.regions === null) return [];

      this.regions.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedInstitution() {
      let results = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.institutions === null) return [];

      this.institutions.forEach((item) => {
        results.push({
          id: item.id,
          name: item.name,
        });
      });
      return results;
    },
    formattedUserGroup() {
      let groups = [];
      if (!(this.user_groups instanceof Array)) {
        return [];
      }
      this.user_groups.forEach((item) => {
        groups.push({
          id: item.id,
          name: item.name,
        });
      });
      return groups;
    },
    formattedPosition() {
      let positions = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.positions === null) return [];

      this.positions.forEach((item) => {
        positions.push({
          id: item.id,
          name: item.name,
        });
      });
      return positions;
    },
    formattedTitle() {
      let titles = [
        {
          id: null,
          name: this.$t("general.choose"),
        },
      ];
      if (this.titles === null) return [];

      this.titles.forEach((item) => {
        titles.push({
          id: item.id,
          name: item.name,
        });
      });
      return titles;
    },
  },
  methods: {
    userLabel(user) {
      return `${user.name} ${user.surname}`
    },
    getUsers() {
      axios.get(`${'api/user'}`, {
        headers: this.apiHeaders
      }).then((result) => {
        this.users = result.data;
        const parent = this.users.find(o => o.id === this.selectedParentId);
        this.selectedUser = parent;
      });
    },
    getRoles() {
      axios
        .get(`${"api/roles"}`, {
          headers: this.apiHeaders,
        })
        .then((result) => {
          this.roles = result.data?.global;
          result.data?.company.forEach((item) => {
            this.roles.push(item);
          });
        });
    },
    addTagToMission(tag) {
      let content = new FormData();
      content.append("status", 1);
      content.append("name", tag.name);

      this.$store
        .dispatch(POSITION_MODULE_NAME + "/" + POSITION_CREATE_ITEM, {
          url: POSITION_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getPositions().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.position = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.position_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    addTagToTitles(tag) {
      let content = this.createFromTagData(tag)

      this.$store
        .dispatch(TITLE_MODULE_NAME + "/" + TITLE_CREATE_ITEM, {
          url: TITLE_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getTitles().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.title = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.title_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    addTagToRegion(tag) {
      let content = this.createFromTagData(tag)

      this.$store
        .dispatch(REGION_MODULE_NAME + "/" + REGION_CREATE_ITEM, {
          url: REGION_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getRegions().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.region = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.region_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    addTagToDepartment(tag) {
      let content = this.createFromTagData(tag)

      this.$store
        .dispatch(DEPARTMENT_MODULE_NAME + "/" + DEPARTMENT_CREATE_ITEM, {
          url: DEPARTMENT_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getDepartments().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.department = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.department_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    addTagToBranch(tag) {
      let content = this.createFromTagData(tag)

      this.$store
        .dispatch(BRANCH_MODULE_NAME + "/" + BRANCH_CREATE_ITEM, {
          url: BRANCH_BASE_URL + '?company_id=' + this.form.company_id,
          contents: content,
        })
        .then(() => {
          this.getBranchs().then((response) => {
            //eklenen tag'i veritabanına ekledikten sonra bul ve Fe'de güncelle
            const addedPosition = response.find(pos => pos.name === tag.name);

            if (addedPosition) {
              this.branch = {
                id: addedPosition.id,
                name: addedPosition.name,
              };
              this.form.branch_id = addedPosition.id;
            }
          });
        })
        .catch((error) => {
          if (this.formErrors) {
            this.formErrors.push(this.$t('general.error_occurred'));
          }
        });
    },
    createFromTagData(tag) {
      let content = new FormData();
      content.append("status", 1);
      content.append("name", tag.name);
      return content
    },

    onFileUpload(files) {
      if (files && files.length > 0) {
        this.uploded_file = true;
        let value = files[0];
        this.form.photo = value;
        let blob = URL.createObjectURL(value);
        let imageHolder = $("#profile-image-holder");
        imageHolder.attr("src", blob);
      }
    },
    triggerFileUpload() {
      if (this.$refs.fileUpload) {
        this.$refs.fileUpload.triggerFileInput();
      }
    },
    getLoginTypes() {
      let types = [];
      axios
        .get(`api/user/login-types`, {
          headers: this.apiHeaders,
        })
        .then((result) => {
          result.data.forEach((data) => {
            types.push({
              id: data.id,
              name: data.name,
            });
          });
        });
      this.login_types = types;
    },
    getAuthenticationTypes() {
      let types = [];
      axios
        .get(`api/user/authentication-types`, {
          headers: this.apiHeaders,
        })
        .then((result) => {
          result.data.forEach((data) => {
            types.push({
              id: data.id,
              name: data.name,
            });
          });
        });
      this.authentication_types = types;
    },
    validateForm() {
      let self = this;
      let form = self.form;
      self.formErrors = [];

      // İsim kontrolü
      if (form.name === null || form.name == "") {
        self.formErrors.push(self.$t("users.required.firstname"));
        self.scrollToTop();
        return false;
      }

      // Soyisim kontrolü
      if (form.surname === null || form.surname == "") {
        self.formErrors.push(self.$t("users.required.lastname"));
        self.scrollToTop();
        return false;
      }

      // Email kontrolü
      if (!self.shouldHideEmailField && (form.email === null || form.email === "")) {
        self.formErrors.push(self.$t("users.required.email"));
        self.scrollToTop();
        return false;
      }

      // Telefon kontrolü
      let hasPhoneSelectedForAnyType =
        form.authentication_type_id === 3 || form.login_type_id === 2;

      let isPhoneRequired =
        hasPhoneSelectedForAnyType && form?.phone?.length === 0;

      if (isPhoneRequired) {
        self.formErrors.push(self.$t("users.required.phone"));
        self.scrollToTop();
        return false;
      }

      if (
        isPhoneRequired &&
        (form?.phone?.length !== 10 || Number.isInteger(form?.phone))
      ) {
        self.formErrors.push(self.$t("users.phone.invalid"));
        self.scrollToTop();
        return false;
      }

      if (
        form.login_type_id === 2 &&
        (form.phone === null || form.phone == "")
      ) {
        self.formErrors.push(self.$t("users.required.phone"));
        self.scrollToTop();
        return false;
      }

      // TC Kimlik No kontrolü
      if (
        form.login_type_id === 3 &&
        (form.national_id === null || form.national_id == "")
      ) {
        self.formErrors.push(self.$t("general.required_national_id"));
        self.scrollToTop();
        return false;
      }

      // Sicil No kontrolü
      if (
        form.login_type_id === 4 &&
        (form.registration_number === null || form.registration_number == "")
      ) {
        self.formErrors.push(self.$t("users.required.reg_no"));
        self.scrollToTop();
        return false;
      }

      return true;
    },
    formSubmit() {
      let self = this;

      // Eğer zaten submit işlemi çalışıyorsa, yeni submit'i engelle
      if (self.isSubmitting) {
        return false;
      }

      // Validation kontrolleri
      if (!self.validateForm()) {
        return false;
      }

      let form = self.form;
      form.user = form.id;

      // Submit işlemi başladı
      self.isSubmitting = true;

      const parsedPhone = form.phone
        ? this.parsePhonePayload(form.phone, form.country_phone_code)
        : { phone: null, country_phone_code: form.country_phone_code || '90' };

      form.store_id = form.branch_id;
      // form.photo = this.form.photo;
      // form.national_id = self.form.national_id;
      // form.notification_method = self.form.notification_method;
      let form_data = new FormData();
      form_data.append("name", form.name);
      form_data.append("surname", form.surname);
      form_data.append("email", form.email);
      form_data.append("phone", parsedPhone.phone);
      form_data.append("dob", form.dob);
      form_data.append("registration_number", form.registration_number);
      form_data.append("national_id", form.national_id);
      form_data.append("country_phone_code", parsedPhone.country_phone_code);
      // form_data.append("user_group", form.user_group)
      form_data.append("company_id", form.company_id);
      form_data.append("branch_id", form.branch_id);
      form_data.append("department_id", form.department_id);
      form_data.append("region_id", form.region_id);
      form_data.append("title_id", form.title_id);
      form_data.append("gender_id", form.gender_id);
      form_data.append("position_id", form.position_id);
      form_data.append("corporate_id", this.corporate ? this.corporate.id : null);
      form.password ? form_data.append("password", form.password) : null;
      // form.is_admin ? form_data.append("is_admin", form.is_admin) : null;
      form_data.append("login_type_id", form.login_type_id);
      form_data.append("authentication_type_id", form.authentication_type_id);
      form_data.append("notification_method", form.notification_method);
      const languageId = Number(form.language_id);
      if (Number.isInteger(languageId) && languageId > 0) {
        form_data.append("language_id", languageId);
      }
      form.photo ? form_data.append("photo", form.photo) : null;
      form_data.append("_method", "PUT");
      let group_ids = [];
      if (self.userGroup !== null && self.userGroup.length > 0) {
        self.userGroup.forEach((item) => {
          form_data.append("group_ids[]", item.id);
          group_ids.push(item.id);
        });
      } else {
        form_data.append("group_ids[]", null);
      }
      let role_ids = [];
      if (self.selectedRoles && self.selectedRoles.length > 0) {
        self.selectedRoles.forEach((item) => {
          form_data.append("role_ids[]", item.id);
          role_ids.push(item.id);
        });
      } else {
        form_data.append("role_ids[]", "");
      }
      form_data.append("parent_id", self.selectedUser?.id ?? null);
      if (self.selectedIntegrations && self.selectedIntegrations.length > 0) {
        self.selectedIntegrations.forEach((item) => {
          form_data.append("integration_tags[]", item.type);
        });
      } else {
        form_data.append("integration_tags", "");
      }
      store.dispatch(USER_UPDATE_MODULE + "/" + UPDATE_USER_POST, {
        url: USER_UPDATE_BASE_URL + "/update/" + this.$route.params.id,
        contents: form_data,
        returnType: "stateless",
        acceptPromise: true,
      })
        .then(() => {
        })
        .catch((error) => {
          self.formErrors = [];

          const data = error?.response?.data || error;

          if (data?.errors) {
            Object.values(data.errors).forEach((messages) => {
              if (Array.isArray(messages)) {
                self.formErrors.push(...messages);
              } else {
                self.formErrors.push(messages);
              }
            });
          } else if (data?.message) {
            self.formErrors.push(data.message);
          } else if (typeof data === "string") {
            self.formErrors.push(data);
          } else {
            self.formErrors.push(self.$t("general.error_occurred"));
          }

          self.isSubmitting = false;
          self.scrollToTop();
        })
        .finally(() => {
          self.isSubmitting = false;
        });
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      });
    },
    getBranchs() {
      return this.$store.dispatch(BRANCH_MODULE + "/" + GET_BRANCHS, {
        url: BRANCH_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getDepartments() {
      return this.$store.dispatch(DEPARTMENT_MODULE + "/" + GET_DEPARTMENTS, {
        url: DEPARTMENT_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getRegions() {
      return this.$store.dispatch(REGION_MODULE + "/" + GET_REGIONS, {
        url: REGION_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getTitles() {
      return this.$store.dispatch(TITLE_MODULE + "/" + GET_TITLES, {
        url: TITLE_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getGenders() {
      axios.get(`api/genders`, { headers: this.apiHeaders })
        .then((result) => {
          this.genders = result.data.data;
        });
    },
    getSupportedLanguageCodes() {
      return (i18nService.languages || []).map((item) => String(item.lang || "").toLowerCase());
    },
    normalizeLanguageCode(item) {
      return String(item?.code || item?.language || item?.lang || "").toLowerCase();
    },
    getLanguages() {
      const supportedCodes = this.getSupportedLanguageCodes();
      axios
        .get(`api/localization`, { headers: this.apiHeaders })
        .then((result) => {
          const backendLanguages = result?.data?.languages || [];
          this.languages = backendLanguages
            .filter((item) => supportedCodes.includes(this.normalizeLanguageCode(item)))
            .map((item) => ({
              ...item,
              id: Number(item.id),
            }))
            .filter((item) => Number.isInteger(item.id) && item.id > 0);
        });
    },
    getPositions() {
      return this.$store.dispatch(POSITION_MODULE + "/" + GET_POSITIONS, {
        url: POSITION_BASE_URL,
        filters: {
          company_id: this.company_id,
        },
      });
    },
    getInstitutions() {
      return this.$store.dispatch(COMPANY_MODULE + "/" + GET_INSTITUTIONS, {
        url: COMPANY_BASE_URL,
        filters: {
          all: "all",
        },
      });
    },
    getUserGroups() {
      this.$store.dispatch(USER_GROUP_MODULE + "/" + GET_USER_GROUPS, {
        url: USER_GROUP_BASE_URL,
        filters: {
          all: "all",
        },
      });
    },
    getUser() {
      this.$store.dispatch(USER_MODULE + "/" + GET_USER, {
        url: "api/user/detail" + "/" + this.$route.params.id,
      });
    },
    changeCompany() {
      if (this.userDetail.company_id) {
        this.company_id = this.userDetail.company_id;

        let temp = this.userDetail.company_id;
        this.form.company_id = null;
        this.form.company_id = temp;

        this.getPositions();
        this.getTitles();
        this.getRegions();
        this.getDepartments();
        this.getBranchs();

        this.form.position_id = "";
        this.form.title_id = "";
        this.form.region_id = "";
        this.form.department_id = "";
        this.form.branch_id = "";
      }
    },

    getIntegrationOptions() {
      axios.get('api/widgets', {
        params: { with_user_custom_list: 1 },
        headers: this.apiHeaders,
      }).then((result) => {
        const options = Array.isArray(result.data) ? [...result.data] : [];
        if (!options.some(option => option.type === 'gofluent')) {
          options.push({ type: 'gofluent', title: 'Go Fluent' });
        }
        this.integrationOptions = options;
        const tags = this.userDetail?.integration_tags;
        if (Array.isArray(tags)) {
          this.selectedIntegrations = options.filter(option => tags.includes(option.type));
        }
      }).catch(() => {
        const goFluentOption = { type: 'gofluent', title: 'Go Fluent' };
        this.integrationOptions = [goFluentOption];
        if (this.userDetail?.integration_tags?.includes('gofluent')) {
          this.selectedIntegrations = [goFluentOption];
        }
      });
    },
    checkPhoneValidation(value) {
      const phone = value?.phone || '';
      const countryCode = String(value?.country_phone_code || '90').replace(/\D/g, '') || '90';

      if (!phone) {
        this.form.phone = '';
        this.form.country_phone_code = countryCode;
        return;
      }

      // Ulusal numarayı çıkar (ülke kodu / + / boşluk temizle)
      let nationalNumber = String(phone).replace(/\D/g, '');
      if (nationalNumber.startsWith(countryCode)) {
        nationalNumber = nationalNumber.slice(countryCode.length);
      }
      nationalNumber = nationalNumber.replace(/^0/, '');

      // PhoneInput + submit boşluklu uluslararası format bekliyor: "+90 5XXXXXXXXX"
      this.form.country_phone_code = countryCode;
      this.form.phone = nationalNumber
        ? `+${countryCode} ${nationalNumber}`
        : `+${countryCode} `;
    },
    parsePhonePayload(phoneValue, fallbackCountryCode = '90') {
      const raw = String(phoneValue || '').trim();
      const fallbackCode = String(fallbackCountryCode || '90').replace(/\D/g, '') || '90';

      if (!raw) {
        return { phone: null, country_phone_code: fallbackCode };
      }

      // "+90 455 454 55 53" formatı
      if (raw.includes(' ')) {
        const countryCode = raw.substring(0, raw.indexOf(' ')).replace(/\D/g, '') || fallbackCode;
        const nationalNumber = raw.substring(raw.indexOf(' ') + 1).replace(/\D/g, '');
        return {
          country_phone_code: countryCode,
          phone: nationalNumber.length > 5 ? nationalNumber : null,
        };
      }

      // "+904554545553" formatı (boşluksuz)
      const digits = raw.replace(/\D/g, '');
      if (digits.startsWith(fallbackCode) && digits.length > fallbackCode.length) {
        return {
          country_phone_code: fallbackCode,
          phone: digits.slice(fallbackCode.length),
        };
      }

      return {
        country_phone_code: fallbackCode,
        phone: digits.length > 5 ? digits : null,
      };
    },
  },
  mounted() {
    this.getLoginTypes();
    this.getAuthenticationTypes();
    this.getInstitutions();
    this.getUserGroups();
    this.getUser();
    this.getGenders();
    this.getLanguages();
    this.getIntegrationOptions();

    if (!this.$isSuperAdmin) {
      this.form.company_id = this.userDetail.company_id;
      this.changeCompany();
    }
  },
  watch: {
    success: function (val) {
      if (val) {
        this.isSubmitting = false;
        this.$router.push({
          name: "company.user.list",
          params: { succesMessage: this.$t("general.update_success") },
        });
      }
    },
    errors: function (val) {
      if (val) {
        this.isSubmitting = false;
        this.formErrors.push(val);
        this.scrollToTop();
      }
    },
    passwordStatus(value) {
      if (value) {
        $("#pass").prop("disabled", true);
        this.form.password = "12345678";
      } else {
        $("#pass").prop("disabled", false);
      }
      this.form.password = "";
    },
    userDetail(value) {
      if (value && value.company) {
        console.log("userDetail", value)
        this.company_id = value.company.id;
        if (this.company_id) {
          this.form = value;

          // Telefon validasyonu
          this.checkPhoneValidation(value);

          if (value.dob) {
            this.form.dob = moment(this.form.dob).format("YYYY-MM-DD");
          }
          if (value.gender) {
            this.form.gender_id = value.gender.id;
          }
          if ((this.form.language_id === null || this.form.language_id === "") && value.language?.id) {
            this.form.language_id = value.language.id;
          }
          this.company = value.company;
          this.branch = value.branch;
          this.title = value.title;
          this.department = value.department;
          this.position = value.position;
          this.region = value.region;
          this.corporate = value.corporate || null;
          this.userGroup = value.groups;
          value.national_id ? (this.national_id = value.national_id) : null;
          value.notification_method
            ? (this.notification_method = value.notification_method)
            : null;
          value.roles.forEach((data) => {
            this.selectedRoles.push(data);
          });
          this.selectedParentId = value.parent_id;
          // Mevcut entegrasyon tag'lerini doldur
          if (value.integration_tags && Array.isArray(value.integration_tags)) {
            this.selectedIntegrations = this.integrationOptions.filter(
              opt => value.integration_tags.includes(opt.type)
            );
          }
          this.getBranchs();
          this.getPositions();
          this.getTitles();
          this.getRegions();
          this.getDepartments();
          this.getRoles();
          this.getUsers();

        }
      }
    },
    title(value) {
      // Ünvan
      if (value && value.id) {
        this.form.title_id = value.id;
      } else {
        this.form.title_id = null;
      }
    },
    position(value) {
      // Görev alanı
      if (value && value.id) {
        this.form.position_id = value.id;
      } else {
        this.form.position_id = null;
      }
    },
    region(value) {
      //  Gm/Bölge
      if (value && value.id) {
        this.form.region_id = value.id;
      } else {
        this.form.region_id = null;
      }
    },
    department(value) {
      // BÖlüm/Department
      if (value && value.id) {
        this.form.department_id = value.id;
      } else {
        this.form.department_id = null;
      }
    },
    branch(value) {
      // Şube/Mağaza/Distribütör
      if (value && value.id) {
        this.form.branch_id = value.id;
      } else {
        this.form.branch_id = null;
      }
    },
    corporate(value) {
      this.form.corporate_id = value ? value.id : null;
    },

  },
};
</script>
<style scoped>
input[type=file],
input[type=file]::-webkit-file-upload-button {
  cursor: pointer;
}

.uk-alert {
  margin: 0;
}

/* fileUpload component styling for user photo */
.user-photo-upload :deep(.preview-area) {
  display: none;
}

.user-photo-upload :deep(.file-upload-container) {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  opacity: 0;
  pointer-events: none;
}

.user-photo-upload :deep(.btn) {
  display: none;
}

.user-photo-camera-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  cursor: pointer;
  pointer-events: auto;
}
</style>

<style>
.multiselect__tag {
  color: white;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: #3e416d;
}

.multiselect__tag-icon {
  background-color: transparent;
  line-height: 2.2;
}

.multiselect__tag-icon:after {
  color: #fff;
}

.multiselect__tag-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.multiselect__input {
  border: unset !important;
}

.multiselect__tags .multiselect__input {
  border: unset !important;
}
</style>
